import { useEffect } from 'react'
import { preloadSiteContent } from '@/hooks/useSiteContent'
import EditableText from '@/components/EditableText'

export default function Comprendre() {
  useEffect(() => {
    preloadSiteContent()
  }, [])

  const pistes = [
    {
      titre: 'Fréquences et perceptions',
      intro: 'Tout vibre, à sa manière.',
      texte: 'On peut observer que ce que l\'on perçoit dépend de ce vers quoi notre attention se tourne. Les sons, les lumières, les textures, les silences — tout porte une fréquence. Certaines résonnent, d\'autres non. Cela peut favoriser une forme d\'écoute plus fine, moins encombrée par le mental.',
      citation: 'Ce qui se perçoit varie selon l\'état intérieur de celui qui observe.'
    },
    {
      titre: 'États de conscience',
      intro: 'Il existe plusieurs manières d\'être présent.',
      texte: 'Entre le sommeil profond et la veille attentive, de nombreux états intermédiaires existent. Dans l\'expérience, on peut remarquer qu\'aucun n\'est supérieur à un autre — chacun ouvre une porte différente sur la perception de soi et du monde.',
      citation: 'La conscience n\'est pas une ligne, plutôt un paysage.'
    },
    {
      titre: 'Corps · mental · ressenti',
      intro: 'Trois voix qui se parlent parfois, s\'ignorent souvent.',
      texte: 'Le corps garde trace de ce que le mental a oublié. Le ressenti, lui, se tient entre les deux, comme un messager. Prêter attention à ces trois dimensions, sans les hiérarchiser, peut favoriser une forme de cohérence intérieure.',
      citation: 'Le corps sait ce que l\'esprit n\'a pas encore nommé.'
    },
    {
      titre: 'Cycles naturels',
      intro: 'Les saisons, les rythmes, les respirations.',
      texte: 'Il se peut que ce que l\'on vit intérieurement soit en dialogue avec les cycles extérieurs — les saisons, les phases de la lune, les rythmes du jour et de la nuit. Sans rien affirmer, on peut se rendre disponible à ces correspondances et observer ce qui émerge.',
      citation: 'Rien dans la nature ne force, tout se déploie.'
    },
    {
      titre: 'Influence de l\'environnement',
      intro: 'Ce qui nous entoure nous habite.',
      texte: 'Les espaces, les lumières, les personnes, les sons que nous traversons — tout cela laisse une empreinte. Dans l\'expérience, on peut remarquer qu\'un lieu silencieux, une lumière douce ou une présence bienveillante modifient subtilement la qualité de notre présence.',
      citation: 'L\'environnement n\'est pas un décor, il est une extension du ressenti.'
    }
  ]

  return (
    <div className="fade-in">
      {/* Intro */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <EditableText pageKey="comprendre" sectionKey="eyebrow" fallback="— Première porte —" as="p" contentType="subtitle" className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8" />
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            <EditableText pageKey="comprendre" sectionKey="page_title" fallback="Comprendre" as="span" contentType="title" />
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <EditableText
            pageKey="comprendre"
            sectionKey="page_intro"
            fallback={"Des pistes de lecture, jamais des vérités.\nUn regard posé, sans chercher à saisir."}
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose italic whitespace-pre-line"
          />
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 pb-32">
        <div className="max-w-4xl mx-auto space-y-32">
          {pistes.map((piste, i) => (
            <article key={i} className="group">
              <div className="flex flex-col md:flex-row gap-8 md:gap-16">
                {/* Number */}
                <div className="md:w-24 flex-shrink-0">
                  <p className="font-serif text-6xl text-[#c9a96e]/30 group-hover:text-[#c9a96e]/60 transition-colors duration-1000">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-6">
                  <h2 className="font-serif text-3xl md:text-4xl font-light text-[#f5f1ea]">
                    <EditableText pageKey="comprendre" sectionKey={`piste_${i + 1}_titre`} fallback={piste.titre} as="span" contentType="title" />
                  </h2>
                  <p className="font-serif text-lg italic text-[#c9a96e]/80">
                    <EditableText pageKey="comprendre" sectionKey={`piste_${i + 1}_intro`} fallback={piste.intro} as="span" contentType="subtitle" />
                  </p>
                  <div className="w-12 h-px bg-[#c9a96e]/30" />
                  <p className="text-base md:text-lg text-[#f5f1ea]/75 leading-loose font-light">
                    <EditableText pageKey="comprendre" sectionKey={`piste_${i + 1}_texte`} fallback={piste.texte} as="span" contentType="paragraph" multiline />
                  </p>
                  <blockquote className="border-l border-[#c9a96e]/40 pl-6 mt-8">
                    <p className="font-serif italic text-[#a8a39e] text-lg leading-relaxed">
                      « <EditableText pageKey="comprendre" sectionKey={`piste_${i + 1}_citation`} fallback={piste.citation} as="span" contentType="paragraph" /> »
                    </p>
                  </blockquote>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-2xl mx-auto text-center">
          <EditableText
            pageKey="comprendre"
            sectionKey="closing_text"
            fallback={"Rien ici n'est à prendre comme une affirmation.\nTout est une invitation à observer par soi-même."}
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif italic text-xl md:text-2xl text-[#f5f1ea]/70 leading-loose whitespace-pre-line"
          />
        </div>
      </section>
    </div>
  )
}
